import React, { useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  Container,
  Paper,
  Divider,
  alpha,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { BiExpand, BiPlayCircle, BiFile, BiCertification } from 'react-icons/bi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import parse from 'html-react-parser';
import {
  useFetchThisCourseIdQuery,
  useListAllCourseQuery,
  useUserCourseEnrollQuery
} from 'redux/reducers/user/dashboard/tms-course';
import { backendURL } from 'redux/reducers/settings';

function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { data: allCourse, isLoading: allcourseLoading, refetch } = useListAllCourseQuery();
  const { mutate: EnrollCourse, isSuccess } = useUserCourseEnrollQuery();
  const { data: sessionData, isLoading: isSessionLoading } = useFetchThisCourseIdQuery(id);

  // Find the specific course details from the list
  const selectedCourse = allCourse?.find((item) => item.id === parseInt(id as string));

  const handleEnroll = () => {
    try {
      EnrollCourse({ course: parseInt(id as string) });
    } catch (error) {
      toast.error('Failed to Enroll');
    }
  };

  useEffect(() => {
    if (isSuccess) {
      navigate(`/course/enroll/view/${id}`);
      refetch();
    }
  }, [isSuccess, id, navigate, refetch]);

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pb: 10 }}>
      {/* --- MODERN HERO HEADER --- */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #0B3F6B 0%, #062844 100%)', 
          color: 'white', 
          pt: { xs: 6, md: 10 }, 
          pb: { xs: 6, md: 10 } 
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box 
                component="img"
                src={backendURL+ selectedCourse?.image || 'https://www.nasc.org.np/sites/default/files/styles/650xauto-news/public/default_images/news.jpg?itok=caWSylYn'}
                sx={{ 
                  width: '100%', 
                  borderRadius: 4, 
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                  aspectRatio: '1/1',
                  objectFit: 'cover'
                }}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.8, fontWeight: 700 }}>
                COURSE DETAILS
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
                {selectedCourse?.title}
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                {selectedCourse?.is_enrolled === false ? (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleEnroll}
                    sx={{ 
                      bgcolor: 'primary.main', px: 4, py: 1.5, borderRadius: 2, fontWeight: 700,
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    Enroll in Course
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate(`/course/enroll/view/${id}`)}
                    sx={{ bgcolor: '#fff', color: '#0B3F6B', px: 4, py: 1.5, borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: '#f0f0f0' } }}
                  >
                    Continue Learning
                  </Button>
                )}

                {/* <Button
                  variant="outlined"
                  size="large"
                  startIcon={<BiCertification />}
                  onClick={() => navigate('/certificate')}
                  sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', px: 4, borderRadius: 2, fontWeight: 700, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  Download Certificate
                </Button> */}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* --- CONTENT SECTION --- */}
      <Container maxWidth="lg" sx={{ mt: -4 }}>
        <Grid container spacing={4}>
          {/* Main Info */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider', mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
                What you’ll learn
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {selectedCourse?.description ? parse(selectedCourse.description) : 'No description available for this course.'}
              </Typography>
            </Paper>

            <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
              Course Curriculum
            </Typography>

            {isSessionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                {sessionData?.map((item: any, index: number) => (
                  <Accordion 
                    key={index} 
                    elevation={0} 
                    sx={{ 
                      '&:before': { display: 'none' },
                      borderBottom: index !== sessionData.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<BiExpand />}
                      sx={{ py: 1, '&.Mui-expanded': { bgcolor: alpha('#0B3F6B', 0.03) } }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.light', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.8rem' }}>
                          {index + 1}
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{item.title}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 4, pb: 3 }}>
                      <List disablePadding>
                        <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                            <BiFile size={20} color={alpha('#000', 0.4)} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                {item.description ? parse(item.description) : 'No session details available.'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Grid>

          {/* Sidebar / Stats */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider', position: 'sticky', top: 24 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                This course includes:
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <BiPlayCircle size={20} color="#0B3F6B" />
                  <Typography variant="body2">{sessionData?.length || 0} Sessions</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" spacing={2} alignItems="center">
                  <BiCertification size={20} color="#0B3F6B" />
                  <Typography variant="body2">Professional Certificate</Typography>
                </Stack>
                <Divider />
                <Typography variant="caption" color="text.secondary">
                  Access on mobile and TV. Lifetime access for enrolled members.
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default CourseDetail;